import type {
  ForestTreePlacementV2,
  LegacyPlantSnapshotV2,
  PlantGardenV2Snapshot,
  PlantInstanceV2,
  PlantNourishmentResultV2,
  PlantRewardResultV2,
  PlantV2MutationResult,
} from '@/entities/plant-garden-v2'
import { createNourishmentEvent, nourishPlant, projectPlantVitality } from '@/entities/plant-lifecycle'
import { createLearningTaskCompletionKey } from '@/entities/progress'
import { readStudentWorkspaceFacts } from '@/store/selectors/workspaceFacts'
import {
  createEmptyPlantGardenState,
  normalizePlantGardenState,
  type BrowserPlantGardenV2Record,
  useStudentPlantGardenAuthorityStore,
  useStudentPlantGardenV2AuthorityStore,
} from '@/store/plantStore'
import type { PlantGardenV2Repository } from './PlantGardenV2Repository'

const SLOT_CAPACITY = 12
const MAX_MVP_PARCELS = 2
const MAX_MVP_PLACEMENTS = SLOT_CAPACITY * MAX_MVP_PARCELS
const REWARD_AMOUNTS = {
  'learning-task': 20,
  'formative-task': 10,
  'graduation-assessment': 30,
} as const

function stableHash(value: string): string {
  let hash = 0x811c9dc5
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 0x01000193)
  }
  return (hash >>> 0).toString(16).padStart(8, '0')
}

function businessDate(value: string | number | Date): string {
  const date = value instanceof Date ? value : new Date(value)
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(date)
}

function createInstanceId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return `plant-v2:${crypto.randomUUID()}`
  return `plant-v2:${Date.now()}:${Math.random().toString(36).slice(2, 10)}`
}

function saveRecord(studentId: string, record: BrowserPlantGardenV2Record) {
  const state = useStudentPlantGardenV2AuthorityStore.getState()
  useStudentPlantGardenV2AuthorityStore.setState({
    recordsByStudent: { ...state.recordsByStudent, [studentId]: record },
  })
}

function migrateLegacyGarden(studentId: string): BrowserPlantGardenV2Record {
  const legacyState = useStudentPlantGardenAuthorityStore.getState().gardensByStudent[studentId]
  const legacy = normalizePlantGardenState(legacyState ?? createEmptyPlantGardenState())
  const migratedAt = new Date().toISOString()
  const legacySnapshots: LegacyPlantSnapshotV2[] = []

  for (const [zone, plant] of Object.entries(legacy.gardens)) {
    legacySnapshots.push({
      legacyTreeId: `legacy-v2:${stableHash(`${studentId}:zone-current:${zone}`)}`,
      sourceKind: 'zone-current',
      sourceId: zone,
      zone,
      species: plant.species,
      exp: plant.exp,
      growthExp: plant.growthExp,
      stage: plant.stage,
      plantedAt: plant.plantedAt,
      endedAt: null,
      endReason: 'migration-snapshot',
      uprootedAt: null,
      placement: null,
    })
  }
  for (const entry of legacy.history) {
    legacySnapshots.push({
      legacyTreeId: `legacy-v2:${stableHash(`${studentId}:history:${entry.instanceId}`)}`,
      sourceKind: 'history',
      sourceId: entry.instanceId,
      zone: entry.zone,
      species: entry.species,
      exp: entry.finalGrowthExp,
      growthExp: entry.finalGrowthExp,
      stage: entry.finalGrowthExp >= 800 ? 'mature' : entry.finalGrowthExp >= 300 ? 'growing' : 'seedling',
      plantedAt: entry.plantedAt,
      endedAt: entry.endedAt,
      endReason: entry.endReason,
      uprootedAt: null,
      placement: null,
    })
  }
  for (const entry of Object.values(legacy.album)) {
    if (!entry) continue
    legacySnapshots.push({
      legacyTreeId: `legacy-v2:${stableHash(`${studentId}:album:${entry.species}`)}`,
      sourceKind: 'album',
      sourceId: entry.species,
      zone: null,
      species: entry.species,
      exp: 0,
      growthExp: 0,
      stage: null,
      plantedAt: entry.lastPlantedAt,
      endedAt: null,
      endReason: null,
      uprootedAt: null,
      placement: null,
    })
  }
  const visible = legacySnapshots.filter((entry) => entry.sourceKind !== 'album' && entry.species)
  const placements: ForestTreePlacementV2[] = visible.slice(0, MAX_MVP_PLACEMENTS).map((entry, index) => ({
    treeOrigin: 'legacy',
    treeId: entry.legacyTreeId,
    parcelIndex: Math.floor(index / SLOT_CAPACITY),
    slotIndex: index % SLOT_CAPACITY,
    placedAt: migratedAt,
  }))
  const placementMap = new Map(placements.map((placement) => [placement.treeId, placement]))
  const snapshots = legacySnapshots.map((entry) => ({
    ...entry,
    placement: placementMap.get(entry.legacyTreeId) ?? null,
  }))
  const parcelCount = visible.length >= SLOT_CAPACITY ? MAX_MVP_PARCELS : 1
  const snapshot: PlantGardenV2Snapshot = {
    version: 2,
    revision: 0,
    lifetimeExp: Object.values(legacy.gardens).reduce((sum, plant) => sum + plant.exp, 0),
    activeInstanceId: null,
    activePlant: null,
    instances: [],
    legacySnapshots: snapshots,
    parcels: Array.from({ length: parcelCount }, (_, parcelIndex) => ({
      parcelIndex,
      terrain: 'plain',
      slotCapacity: SLOT_CAPACITY,
      occupiedSlots: placements.filter((placement) => placement.parcelIndex === parcelIndex).length,
      unlockedAt: migratedAt,
    })),
    placements,
    nextParcelUnlockable: false,
    todayExp: 0,
    businessDate: businessDate(migratedAt),
    migratedAt,
  }
  return {
    snapshot,
    rewardReceipts: {},
    rewardCompletionClaims: {},
    nourishmentReceipts: {},
    nourishmentQuestionReceipts: {},
  }
}

function refreshRecord(record: BrowserPlantGardenV2Record, observedAt = new Date()): BrowserPlantGardenV2Record {
  const occupiedSnapshot = withOccupancy(record.snapshot, observedAt.toISOString())
  let snapshot = occupiedSnapshot.parcels.length === record.snapshot.parcels.length
    ? occupiedSnapshot
    : { ...occupiedSnapshot, revision: occupiedSnapshot.revision + 1 }
  if (snapshot.activePlant) {
    const vitality = projectPlantVitality({
      species: snapshot.activePlant.species,
      nutrient: snapshot.activePlant.nutrient,
      lastNourishedAt: snapshot.activePlant.lastNourishedAt,
    }, observedAt)
    if (vitality.status === 'dead') {
      const endedAt = observedAt.toISOString()
      const instances = snapshot.instances.map((instance) => instance.instanceId === snapshot.activeInstanceId
        ? { ...instance, lifecycleStatus: 'dead' as const, nutrient: 0, endedAt, endReason: 'dead' as const }
        : instance)
      snapshot = {
        ...snapshot,
        revision: snapshot.revision + 1,
        activeInstanceId: null,
        activePlant: null,
        instances,
      }
    } else {
      const activePlant = { ...snapshot.activePlant, nutrient: vitality.nutrient }
      snapshot = {
        ...snapshot,
        activePlant,
        instances: snapshot.instances.map((instance) => instance.instanceId === activePlant.instanceId ? activePlant : instance),
      }
    }
  }
  const currentBusinessDate = businessDate(observedAt)
  const todayExp = Object.values(record.rewardReceipts)
    .filter((receipt) => businessDate(receipt.occurredAt) === currentBusinessDate)
    .reduce((sum, receipt) => sum + receipt.amount, 0)
  return { ...record, snapshot: { ...snapshot, businessDate: currentBusinessDate, todayExp } }
}

function recordFor(studentId: string): BrowserPlantGardenV2Record {
  const existing = useStudentPlantGardenV2AuthorityStore.getState().recordsByStudent[studentId]
  const normalized = existing
    ? {
        ...existing,
        rewardReceipts: existing.rewardReceipts ?? {},
        rewardCompletionClaims: existing.rewardCompletionClaims ?? {},
        nourishmentReceipts: existing.nourishmentReceipts ?? {},
        nourishmentQuestionReceipts: existing.nourishmentQuestionReceipts ?? {},
      }
    : migrateLegacyGarden(studentId)
  const record = refreshRecord(normalized)
  saveRecord(studentId, record)
  return record
}

function withOccupancy(
  snapshot: PlantGardenV2Snapshot,
  unlockedAt = new Date().toISOString(),
): PlantGardenV2Snapshot {
  let parcels = snapshot.parcels.map((parcel) => ({
    ...parcel,
    occupiedSlots: snapshot.placements.filter((placement) => placement.parcelIndex === parcel.parcelIndex).length,
  }))
  const first = parcels.find((parcel) => parcel.parcelIndex === 0)
  if (first && first.occupiedSlots >= first.slotCapacity && !parcels.some((parcel) => parcel.parcelIndex === 1)) {
    parcels = [...parcels, {
      parcelIndex: 1,
      terrain: 'plain',
      slotCapacity: SLOT_CAPACITY,
      occupiedSlots: 0,
      unlockedAt,
    }]
  }
  return {
    ...snapshot,
    parcels,
    nextParcelUnlockable: false,
  }
}

function workspaceFor(studentId: string) {
  const workspace = readStudentWorkspaceFacts(studentId)
  if (!workspace) throw new Error('The active Student Workspace is unavailable for the browser demo Adapter.')
  return workspace
}

function resolveClaim(studentId: string, rewardClaimId: string) {
  const workspace = workspaceFor(studentId)
  const claim = workspace.rewardClaims.find((candidate) => candidate.id === rewardClaimId)
  if (!claim || claim.studentId !== studentId) throw new Error('Reward Claim is not available for this learner.')
  let occurredAt: string
  let completionKey: string
  if (claim.kind === 'graduation-assessment') {
    const session = workspace.assessmentSessions.find((candidate) => candidate.id === claim.assessmentSessionId)
    if (!session || session.status !== 'completed' || !session.completedAt) throw new Error('Graduation Reward Claim is incomplete.')
    occurredAt = session.completedAt
    completionKey = `graduation:${studentId}:${claim.stageId}`
  } else if (claim.kind === 'formative-task') {
    const session = workspace.formativeTaskSessions.find((candidate) => candidate.id === claim.formativeTaskSessionId)
    if (!session || session.phase !== 'completed' || !session.completedAt) throw new Error('Formative Reward Claim is incomplete.')
    occurredAt = session.completedAt
    completionKey = `task:${studentId}:${claim.stageId}:${claim.taskId}`
  } else {
    const taskKey = createLearningTaskCompletionKey(claim.stageId, claim.taskId)
    if (!workspace.completedTasks[taskKey] || !workspace.taskCompletedAt[taskKey]) {
      throw new Error('Learning Task Reward Claim is incomplete.')
    }
    occurredAt = workspace.taskCompletedAt[taskKey]
    completionKey = `task:${studentId}:${claim.stageId}:${claim.taskId}`
  }
  return { claim, occurredAt, completionKey, amount: REWARD_AMOUNTS[claim.kind] }
}

function conflict(expectedRevision: number, revision: number): PlantV2MutationResult {
  return { status: 'conflict', expectedRevision, revision }
}

function readGridPosition(input: { gridX?: number; gridY?: number }) {
  const hasX = input.gridX !== undefined
  const hasY = input.gridY !== undefined
  if (!hasX && !hasY) return null
  if (!hasX || !hasY
      || !Number.isSafeInteger(input.gridX) || !Number.isSafeInteger(input.gridY)
      || input.gridX! < 0 || input.gridY! < 0 || input.gridX! > 255 || input.gridY! > 255) {
    throw new Error('The forest position is invalid.')
  }
  return { gridX: input.gridX!, gridY: input.gridY! }
}

export const browserPlantGardenV2Repository: PlantGardenV2Repository = {
  async loadGarden(studentId) {
    return recordFor(studentId).snapshot
  },

  async applyRewardClaim(studentId, rewardClaimId) {
    const current = recordFor(studentId)
    const duplicate = current.rewardReceipts[rewardClaimId]
    if (duplicate) return { ...duplicate, status: 'duplicate' }
    const source = resolveClaim(studentId, rewardClaimId)
    const priorClaimId = current.rewardCompletionClaims[source.completionKey]
    if (priorClaimId && priorClaimId !== rewardClaimId) {
      throw new Error('This learning task already produced a plant reward.')
    }
    let activePlant = current.snapshot.activePlant
    let instances = current.snapshot.instances
    let activeInstanceId = current.snapshot.activeInstanceId
    if (activePlant) {
      const growthExp = activePlant.growthExp + source.amount
      const matured = growthExp >= 800
      activePlant = {
        ...activePlant,
        growthExp,
        stage: matured ? 'mature' : growthExp >= 300 ? 'growing' : 'seedling',
        lifecycleStatus: matured ? 'matured' : 'active',
        endedAt: matured ? new Date().toISOString() : null,
        endReason: matured ? 'matured' : null,
      }
      instances = instances.map((instance) => instance.instanceId === activePlant!.instanceId ? activePlant! : instance)
      if (matured) activeInstanceId = null
    }
    const revision = current.snapshot.revision + 1
    const receipt: PlantRewardResultV2 = {
      status: 'applied',
      eventId: `plant-exp-v2:${stableHash(rewardClaimId)}`,
      rewardClaimId,
      kind: source.claim.kind,
      amount: source.amount,
      policyVersion: 'plant-reward-v1',
      occurredAt: source.occurredAt,
      revision,
    }
    const next = refreshRecord({
      ...current,
      snapshot: {
        ...current.snapshot,
        revision,
        lifetimeExp: current.snapshot.lifetimeExp + source.amount,
        activeInstanceId,
        activePlant: activeInstanceId ? activePlant : null,
        instances,
      },
      rewardReceipts: { ...current.rewardReceipts, [rewardClaimId]: receipt },
      rewardCompletionClaims: { ...current.rewardCompletionClaims, [source.completionKey]: rewardClaimId },
    })
    saveRecord(studentId, next)
    return receipt
  },

  async applyNourishmentAttempt(studentId, attemptId) {
    const current = recordFor(studentId)
    const duplicate = current.nourishmentReceipts[attemptId]
    if (duplicate) return { ...duplicate, status: 'duplicate' }
    const attempt = workspaceFor(studentId).attempts.find((candidate) => candidate.id === attemptId)
    if (!attempt || attempt.studentId !== studentId) throw new Error('Nourishment attempt is unavailable for this learner.')
    const event = createNourishmentEvent(attempt)
    if (!event) throw new Error('Nourishment requires an eligible first formative attempt.')
    const sameQuestion = current.nourishmentQuestionReceipts[event.sourceId]
    if (sameQuestion) return { ...sameQuestion, status: 'duplicate' }

    let activePlant = current.snapshot.activePlant
    let outcome: 'applied' | 'ignored' = 'ignored'
    if (activePlant && new Date(event.occurredAt).getTime() >= new Date(activePlant.plantedAt).getTime()) {
      const result = nourishPlant({
        species: activePlant.species,
        exp: current.snapshot.lifetimeExp,
        growthExp: activePlant.growthExp,
        stage: activePlant.stage,
        plantedAt: activePlant.plantedAt,
        instanceId: activePlant.instanceId,
        nutrient: activePlant.nutrient,
        lastNourishedAt: activePlant.lastNourishedAt,
      }, event.occurredAt, 12)
      if (result.applied) {
        outcome = 'applied'
        activePlant = {
          ...activePlant,
          nutrient: result.plant.nutrient,
          lastNourishedAt: result.plant.lastNourishedAt ?? activePlant.lastNourishedAt,
        }
      }
    }
    const revision = current.snapshot.revision + 1
    const receipt: PlantNourishmentResultV2 = {
      status: outcome,
      eventId: `plant-nourish-v2:${stableHash(attemptId)}`,
      attemptId,
      amount: 12,
      policyVersion: 'plant-health-v1',
      occurredAt: event.occurredAt,
      outcome,
      revision,
    }
    const instances = activePlant
      ? current.snapshot.instances.map((instance) => instance.instanceId === activePlant!.instanceId ? activePlant! : instance)
      : current.snapshot.instances
    const next = {
      ...current,
      snapshot: { ...current.snapshot, revision, activePlant, instances },
      nourishmentReceipts: { ...current.nourishmentReceipts, [attemptId]: receipt },
      nourishmentQuestionReceipts: { ...current.nourishmentQuestionReceipts, [event.sourceId]: receipt },
    }
    saveRecord(studentId, next)
    return receipt
  },

  async plantSeed(studentId, input) {
    const current = recordFor(studentId)
    if (input.expectedRevision !== current.snapshot.revision) return conflict(input.expectedRevision, current.snapshot.revision)
    if (current.snapshot.activePlant) throw new Error('A global active plant already exists.')
    const parcel = current.snapshot.parcels.find((candidate) => candidate.parcelIndex === input.parcelIndex)
    if (!parcel || input.slotIndex < 0 || input.slotIndex >= parcel.slotCapacity) throw new Error('The planting slot is unavailable.')
    if (current.snapshot.placements.some((placement) => placement.parcelIndex === input.parcelIndex && placement.slotIndex === input.slotIndex)) {
      throw new Error('The planting slot is occupied.')
    }
    const position = readGridPosition(input)
    const plantedAt = new Date().toISOString()
    const instanceId = createInstanceId()
    const placement: ForestTreePlacementV2 = {
      treeOrigin: 'lifecycle', treeId: instanceId,
      parcelIndex: input.parcelIndex, slotIndex: input.slotIndex, placedAt: plantedAt,
      ...(position ?? {}),
    }
    const activePlant: PlantInstanceV2 = {
      instanceId,
      species: input.species,
      lifecycleStatus: 'active',
      growthExp: 0,
      stage: 'seedling',
      nutrient: 80,
      lastNourishedAt: plantedAt,
      plantedAt,
      endedAt: null,
      endReason: null,
      uprootedAt: null,
      placement,
    }
    const revision = current.snapshot.revision + 1
    const snapshot = withOccupancy({
      ...current.snapshot,
      revision,
      activeInstanceId: instanceId,
      activePlant,
      instances: [...current.snapshot.instances, activePlant],
      placements: [...current.snapshot.placements, placement],
    }, plantedAt)
    saveRecord(studentId, { ...current, snapshot })
    return { status: 'planted', instanceId, revision }
  },

  async moveTree(studentId, input) {
    const current = recordFor(studentId)
    if (input.expectedRevision !== current.snapshot.revision) return conflict(input.expectedRevision, current.snapshot.revision)
    const sourceIndex = current.snapshot.placements.findIndex((placement) => (
      placement.treeOrigin === input.treeOrigin && placement.treeId === input.treeId
    ))
    if (sourceIndex < 0) throw new Error('The selected tree is not placed.')
    const parcel = current.snapshot.parcels.find((candidate) => candidate.parcelIndex === input.parcelIndex)
    if (!parcel || input.slotIndex < 0 || input.slotIndex >= parcel.slotCapacity) throw new Error('The target slot is unavailable.')
    const occupied = current.snapshot.placements.find((placement, index) => (
      index !== sourceIndex && placement.parcelIndex === input.parcelIndex && placement.slotIndex === input.slotIndex
    ))
    if (occupied) throw new Error('The target slot is occupied.')
    const position = readGridPosition(input)
    const placement = {
      ...current.snapshot.placements[sourceIndex],
      parcelIndex: input.parcelIndex,
      slotIndex: input.slotIndex,
      ...(position ?? {}),
    }
    const placements = current.snapshot.placements.map((entry, index) => index === sourceIndex ? placement : entry)
    const instances = current.snapshot.instances.map((instance) => instance.instanceId === input.treeId ? { ...instance, placement } : instance)
    const legacySnapshots = current.snapshot.legacySnapshots.map((entry) => entry.legacyTreeId === input.treeId ? { ...entry, placement } : entry)
    const activePlant = input.treeOrigin === 'lifecycle' && current.snapshot.activePlant?.instanceId === input.treeId
      ? { ...current.snapshot.activePlant, placement }
      : current.snapshot.activePlant
    const revision = current.snapshot.revision + 1
    const snapshot = withOccupancy({ ...current.snapshot, revision, placements, instances, legacySnapshots, activePlant })
    saveRecord(studentId, { ...current, snapshot })
    return { status: 'moved', treeId: input.treeId, treeOrigin: input.treeOrigin, revision }
  },

  async uprootDeadTree(studentId, input) {
    const current = recordFor(studentId)
    if (input.expectedRevision !== current.snapshot.revision) return conflict(input.expectedRevision, current.snapshot.revision)
    const uprootedAt = new Date().toISOString()
    let found = false
    const instances = current.snapshot.instances.map((instance) => {
      if (input.treeOrigin !== 'lifecycle' || instance.instanceId !== input.treeId) return instance
      if (instance.lifecycleStatus !== 'dead') throw new Error('Only dead trees can be uprooted.')
      found = true
      return { ...instance, uprootedAt, placement: null }
    })
    const legacySnapshots = current.snapshot.legacySnapshots.map((entry) => {
      if (input.treeOrigin !== 'legacy' || entry.legacyTreeId !== input.treeId) return entry
      if (entry.endReason !== 'dead') throw new Error('Only dead trees can be uprooted.')
      found = true
      return { ...entry, uprootedAt, placement: null }
    })
    if (!found) throw new Error('The selected dead tree does not exist.')
    const placements = current.snapshot.placements.filter((placement) => !(
      placement.treeOrigin === input.treeOrigin && placement.treeId === input.treeId
    ))
    const revision = current.snapshot.revision + 1
    const snapshot = withOccupancy({ ...current.snapshot, revision, instances, legacySnapshots, placements })
    saveRecord(studentId, { ...current, snapshot })
    return { status: 'uprooted', treeId: input.treeId, treeOrigin: input.treeOrigin, uprootedAt, revision }
  },

  async unlockNextParcel(studentId, expectedRevision) {
    const current = recordFor(studentId)
    if (expectedRevision !== current.snapshot.revision) return conflict(expectedRevision, current.snapshot.revision)
    if (current.snapshot.parcels.some((parcel) => parcel.parcelIndex === 1)) {
      return { status: 'duplicate', parcelIndex: 1, revision: current.snapshot.revision }
    }
    const first = current.snapshot.parcels.find((parcel) => parcel.parcelIndex === 0)
    if (!first || first.occupiedSlots < first.slotCapacity) throw new Error('The first forest parcel is not full.')
    const unlockedAt = new Date().toISOString()
    const revision = current.snapshot.revision + 1
    const snapshot = withOccupancy({
      ...current.snapshot,
      revision,
      parcels: [...current.snapshot.parcels, {
        parcelIndex: 1,
        terrain: 'plain',
        slotCapacity: SLOT_CAPACITY,
        occupiedSlots: 0,
        unlockedAt,
      }],
      nextParcelUnlockable: false,
    })
    saveRecord(studentId, { ...current, snapshot })
    return { status: 'unlocked', parcelIndex: 1, revision }
  },
}
